import { SERVER_DEBUG_LOG_ENABLED } from './logging-policy';

export interface ServerLogOptions {
  request?: unknown;
  transport?: unknown;
  durationMs?: number;
  id?: number;
}

// Preserve field names and business data; never print credentials or card secrets.
const SECRET_KEY = /^(authorization|cookie|setcookie|password|oldPassword|newPassword|confirmPassword|accessToken|refreshToken|idToken|token|clientSecret|secretHash|secret|apiKey|credential|credentials|codeVerifier|emailOtpCode|smsMfaCode|softwareTokenMfaCode|otpCode|mfaCode|verificationCode|session|cvv|cvc|securityCode|cardNumber|pan|encryptedCard|encryptedData)$/i;

/** Detached, expandable JS object. Protobuf fields are decoded by the generated schema. */
export function toLogObject(value: unknown, ancestors = new WeakSet<object>()): unknown {
  if (typeof value === 'bigint') return value.toString();
  if (value === null || typeof value !== 'object') return value;
  if (ancestors.has(value)) return '[Circular]';
  ancestors.add(value);
  try {
    if ('toObject' in value && typeof value.toObject === 'function') {
      return toLogObject(value.toObject(), ancestors);
    }
    if (value instanceof Date) return value.toISOString();
    if (value instanceof Uint8Array) return Array.from(value);
    if (Array.isArray(value)) return value.map(item => toLogObject(item, ancestors));
    const source = value as Record<string, unknown>;
    const keys = new Set(Object.keys(source));
    if (value instanceof Error) {
      for (const key of ['name', 'message', 'stack', 'cause', 'code', 'details', 'metadata']) {
        if (key in value) keys.add(key);
      }
    }
    return Object.fromEntries([...keys].map(key => [
      key, SECRET_KEY.test(key.replace(/[_-]/g, '')) ? '[REDACTED]' : toLogObject(source[key], ancestors),
    ]));
  } finally {
    ancestors.delete(value);
  }
}

/** Auth protobufs use a generic `code` field for OAuth/MFA/confirmation secrets. */
export function toLogRequestObject(value: unknown, method: string): unknown {
  const snapshot = toLogObject(value);
  if (snapshot && typeof snapshot === 'object' && /auth|mfa|otp|password|confirmation/i.test(method)) {
    const fields = snapshot as Record<string, unknown>;
    if (typeof fields['code'] === 'string') fields['code'] = '[REDACTED]';
  }
  return snapshot;
}

let sequence = 0;
export function nextServerLogId(): number { return ++sequence; }

/** All console output goes through this gate; logging must never break a request. */
function print(scope: string, method: string, payload: unknown, failed: boolean, options: ServerLogOptions): void {
  if (!SERVER_DEBUG_LOG_ENABLED) return;
  let grouped = false;
  try {
    const response = toLogObject(payload);
    const request = options.request === undefined ? undefined : toLogRequestObject(options.request, method);
    const transport = options.transport === undefined ? undefined : toLogObject(options.transport);
    const timing = options.durationMs === undefined ? '' : ` (${options.durationMs} ms)`;
    console.groupCollapsed(`[${scope} #${options.id ?? nextServerLogId()}] ${method} — ${failed ? 'ERRO' : 'RESPOSTA'}${timing}`);
    grouped = true;
    if (request !== undefined) console.log('Requisição:', request);
    if (transport !== undefined) console.log('Transporte:', transport);
    if (failed) console.error('Erro:', response);
    else console.log('Resposta:', response);
  } catch {
    // A diagnostic failure cannot alter application behavior.
  } finally {
    if (grouped) {
      try { console.groupEnd(); } catch { /* Console may be overridden/unavailable. */ }
    }
  }
}

export function logServerSuccess(scope: string, method: string, response: unknown, options: ServerLogOptions = {}): void {
  print(scope, method, response, false, options);
}

export function logServerError(scope: string, method: string, error: unknown, options: ServerLogOptions = {}): void {
  print(scope, method, error, true, options);
}

/** Frontend failures are explicitly separated from responses returned by the backend. */
export function logClientError(...details: unknown[]): void {
  const hasLabel = typeof details[0] === 'string';
  logServerError('FRONTEND', hasLabel ? String(details[0]) : 'Erro inesperado', hasLabel ? details.slice(1) : details);
}
